import type { PersonalColorMap } from '@lemoncloud/epyt-goods-api';
const QUADRANT_GRID = 3;
const QUADRANT_COUNT = 2;
const GRID = QUADRANT_GRID * QUADRANT_COUNT;
const SVG_SIZE = 344;
const GRID_START = 0.5;
const GRID_END = 343.5;
const CELL_SIZE = (GRID_END - GRID_START) / GRID;
const AXIS_INDEX = GRID / 2;
const AXIS_POSITION = GRID_START + AXIS_INDEX * CELL_SIZE;
const DOT_RADIUS = 7;

interface ToneMapProps {
    map$?: PersonalColorMap;
}

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
const mapToSvgCoord = (value: number) => GRID_START + ((value + 100) / 200) * (GRID_END - GRID_START);
const mapYToSvgCoord = (value: number) => GRID_START + ((100 - value) / 200) * (GRID_END - GRID_START);

export const ToneMap = ({ map$ }: ToneMapProps) => {
    const mapX = map$?.x ?? 0;
    const mapY = map$?.y ?? 0;
    const dotX = clamp(mapToSvgCoord(mapX), GRID_START + DOT_RADIUS, GRID_END - DOT_RADIUS);
    const dotY = clamp(mapYToSvgCoord(mapY), GRID_START + DOT_RADIUS, GRID_END - DOT_RADIUS);
    const gridLineIndexes = Array.from({ length: GRID + 1 }, (_, i) => i);

    return (
        <div className="w-full py-6">
            <div className="text-[16px] md:text-[18px] font-bold tracking-tight text-black">컬러 맵</div>
            <div className="mt-6 mx-auto w-full  aspect-square">
                <svg
                    viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
                    className="w-full h-full block"
                    role="img"
                    aria-label="tone-map"
                >
                    <rect x="0" y="0" width={SVG_SIZE} height={SVG_SIZE} fill="white" />

                    {gridLineIndexes.map(index => {
                        if (index === AXIS_INDEX) {
                            return null;
                        }
                        const position = GRID_START + index * CELL_SIZE;
                        return (
                            <line
                                key={`vertical-${index}`}
                                x1={position}
                                y1={GRID_START}
                                x2={position}
                                y2={GRID_END}
                                stroke="#DCDCDD"
                                strokeWidth="1"
                                strokeDasharray="2 2"
                            />
                        );
                    })}

                    {gridLineIndexes.map(index => {
                        if (index === AXIS_INDEX) {
                            return null;
                        }
                        const position = GRID_START + index * CELL_SIZE;
                        return (
                            <line
                                key={`horizontal-${index}`}
                                x1={GRID_START}
                                y1={position}
                                x2={GRID_END}
                                y2={position}
                                stroke="#DCDCDD"
                                strokeWidth="1"
                                strokeDasharray="2 2"
                            />
                        );
                    })}

                    <line x1={AXIS_POSITION} y1={GRID_START} x2={AXIS_POSITION} y2={GRID_END} stroke="#ADADB0" />
                    <line x1={GRID_START} y1={AXIS_POSITION} x2={GRID_END} y2={AXIS_POSITION} stroke="#ADADB0" />

                    <rect x="155.5" y="0.5" width="33" height="24" rx="2" fill="white" />
                    <text x="172" y="12.5" fill="#15151D" fontSize="13" textAnchor="middle" dominantBaseline="middle">
                        밝은
                    </text>

                    <rect x="150" y="318.5" width="44" height="24" rx="2" fill="white" />
                    <text x="172" y="330.5" fill="#15151D" fontSize="13" textAnchor="middle" dominantBaseline="middle">
                        어두운
                    </text>

                    <rect x="0.5" y="158.5" width="33" height="24" rx="2" fill="white" />
                    <text x="17" y="170.5" fill="#15151D" fontSize="13" textAnchor="middle" dominantBaseline="middle">
                        맑은
                    </text>

                    <rect x="310.5" y="158.5" width="33" height="24" rx="2" fill="white" />
                    <text x="327" y="170.5" fill="#15151D" fontSize="13" textAnchor="middle" dominantBaseline="middle">
                        탁한
                    </text>

                    <circle cx={dotX} cy={dotY} r={DOT_RADIUS} fill="#FF4D4F" stroke="white" strokeWidth="2" />
                </svg>
            </div>
        </div>
    );
};
